import type { Niva, PrognoseendringDto } from '@es/shared';
import { formaterBelop, manedsnavn } from '../../felles/format.js';
import { METODENAVN } from './metodenavn.js';

/**
 * Endringsloggen, gjort om til det skjermen viser.
 *
 * Loggen kommer fra serveren som rader med id-er og beløp. Her blir den til
 * setninger et menneske kan lese: «Kari Nordmann satte oktober fra 349 til 471,
 * torsdag kl. 08.42» — med kommentaren under, fordi den er grunnen.
 */

type Overstyringsendring = Extract<PrognoseendringDto, { hva: 'overstyring' }>;
type Metodeendring = Extract<PrognoseendringDto, { hva: 'metode' }>;

/** Endringene i akkurat denne cellen — samme nivå, samme konto, samme måned. */
export function endringerForCelle(
  endringer: readonly PrognoseendringDto[],
  niva: Niva,
  malId: string,
  maned: number,
): Overstyringsendring[] {
  return endringer.filter(
    (e): e is Overstyringsendring =>
      e.hva === 'overstyring' && e.niva === niva && e.malId === malId && e.maned === maned,
  );
}

/** Endringene i metoden for en hovedkonto. */
export function endringerForMetode(
  endringer: readonly PrognoseendringDto[],
  hovedkontoId: string,
): Metodeendring[] {
  return endringer.filter(
    (e): e is Metodeendring => e.hva === 'metode' && e.hovedkontoId === hovedkontoId,
  );
}

// A method the catalogue no longer has keeps its key, so an old entry in the
// log still says something rather than nothing.
const metodenavn = (m: string | null): string =>
  m === null
    ? 'Budsjett'
    : Object.hasOwn(METODENAVN, m)
      ? METODENAVN[m as keyof typeof METODENAVN]
      : m;

/**
 * Hva endringen var, som tekst.
 *
 * Tre slags overstyring: satt, endret og fjernet. «Fra null» ville vært feil å
 * skrive — det var ikke null kroner før, det var ingen overstyring, og cellen
 * sto på det metoden ga.
 */
export function beskrivEndring(e: PrognoseendringDto): string {
  if (e.hva === 'metode') {
    if (e.metodeFra === e.metodeTil) {
      return `Variablene eller låsene i ${metodenavn(e.metodeTil).toLowerCase()} er endret`;
    }
    return `Metode: ${metodenavn(e.metodeFra)} → ${metodenavn(e.metodeTil)}`;
  }
  if (e.fraBelop === null && e.tilBelop !== null) return `Satt til ${formaterBelop(e.tilBelop)}`;
  if (e.tilBelop === null && e.fraBelop !== null) {
    return `Overstyringen på ${formaterBelop(e.fraBelop)} er fjernet`;
  }
  if (e.fraBelop === e.tilBelop) return 'Kommentaren er endret';
  return `${formaterBelop(e.fraBelop ?? '0')} → ${formaterBelop(e.tilBelop ?? '0')}`;
}

/** Hvilken måned en overstyring gjaldt, med stor forbokstav. */
export function manedFor(e: PrognoseendringDto): string | null {
  if (e.hva !== 'overstyring') return null;
  const navn = manedsnavn(e.maned);
  return navn.charAt(0).toUpperCase() + navn.slice(1);
}

const TIDSPUNKT = new Intl.DateTimeFormat('nb-NO', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  // Kommunens tid, ikke nettleserens: en endring gjort kl. 08.42 i Storvik
  // skal ikke stå som 06.42 for en som leser loggen på en maskin i UTC.
  timeZone: 'Europe/Oslo',
});

/** «24.09.2026, 08:42», i norsk tid. */
export function formaterTidspunkt(iso: string): string {
  return TIDSPUNKT.format(new Date(iso));
}
